export const VENAQQI_UI_STATE_KEY = "venaqqi_ui_state_v1";
export const VENAQQI_LAST_ACTIVE_KEY = "venaqqi_last_active_at";
export const VENAQQI_TUTORIAL_COMPLETED_KEY = "venaqqi_tutorial_completed_at";
export const VENAQQI_TUTORIAL_PROGRESS_KEY = "venaqqi_tutorial_progress_v1";
export const VENAQQI_UI_STATE_SCHEMA_VERSION = 2;

const sessionStore = () => typeof window !== "undefined" ? window.sessionStorage : undefined;
const localStore = () => typeof window !== "undefined" ? window.localStorage : undefined;

const safeJsonParse = value => {
  try { return JSON.parse(value || "null"); } catch { return null; }
};

const shortText = (value, max = 500) => String(value || "").slice(0, max);
const optionalId = value => value == null || value === "" ? null : shortText(value, 100);
const finiteNumber = value => Number.isFinite(Number(value)) ? Number(value) : null;
const textList = (value, limit = 5, max = 120) => Array.isArray(value)
  ? value.map(item => shortText(item, max).trim()).filter(Boolean).slice(0, limit)
  : [];

export const normalizeDonDContext = context => {
  if (!context || typeof context !== "object") return null;
  const placeName = shortText(context.placeName || context.name, 160).trim();
  const placeId = optionalId(context.placeId ?? context.id);
  if (!placeName && !placeId) return null;
  return {
    placeId,
    placeName,
    tipo: shortText(context.tipo, 160).trim(),
    comentario: shortText(context.comentario, 500).trim(),
    platillos: textList(context.platillos, 3, 120),
    tambienVisibles: Array.isArray(context.tambienVisibles)
      ? context.tambienVisibles.slice(0, 5).map(place => ({
        name: shortText(place?.name, 160).trim(),
        tipo: shortText(place?.tipo, 160).trim(),
      })).filter(place => place.name)
      : [],
  };
};

const normalizeProfileResumeState = value => value && typeof value === "object" ? {
  tab: shortText(value.tab || "places", 40),
  scrollTop: Math.max(0, finiteNumber(value.scrollTop) || 0),
} : null;

const normalizeProfileContributionDetail = value => {
  if (!value || typeof value !== "object") return null;
  const placeId = optionalId(value.placeId ?? value.place?.id);
  if (!placeId) return null;
  return {
    type: shortText(value.type || "related", 40),
    placeId,
    reviewId: optionalId(value.reviewId),
  };
};

export const normalizeUiState = state => ({
  schemaVersion: VENAQQI_UI_STATE_SCHEMA_VERSION,
  scrollY: Math.max(0, finiteNumber(state?.scrollY) || 0),
  selectedPlaceId: optionalId(state?.selectedPlaceId),
  alternatePlaceId: optionalId(state?.alternatePlaceId),
  isSuerte: !!state?.isSuerte,
  suerteSlotIds: Array.isArray(state?.suerteSlotIds) ? state.suerteSlotIds.map(optionalId).filter(Boolean).slice(0, 8) : [],
  showProfile: !!state?.showProfile,
  profileResumeState: normalizeProfileResumeState(state?.profileResumeState),
  profileContributionDetail: normalizeProfileContributionDetail(state?.profileContributionDetail),
  showAddPlace: !!state?.showAddPlace,
  currentPage: Math.max(1, Math.floor(finiteNumber(state?.currentPage) || 1)),
  smartSearch: shortText(state?.smartSearch, 300),
  filter: shortText(state?.filter || "Todos", 80) || "Todos",
  chatOpen: !!state?.chatOpen,
  donDContext: normalizeDonDContext(state?.donDContext),
  donDContextPlaceId: optionalId(state?.donDContextPlaceId ?? state?.donDContext?.placeId ?? state?.donDContext?.id),
  savedAt: finiteNumber(state?.savedAt) || Date.now(),
});

export const saveUiState = (state, storage = sessionStore(), now = Date.now()) => {
  const normalized = normalizeUiState({ ...state, savedAt: now });
  try { storage?.setItem(VENAQQI_UI_STATE_KEY, JSON.stringify(normalized)); } catch { /* storage unavailable */ }
  return normalized;
};

export const readUiState = (storage = sessionStore(), maxAgeMs = 24 * 60 * 60 * 1000, now = Date.now()) => {
  let parsed;
  try { parsed = safeJsonParse(storage?.getItem(VENAQQI_UI_STATE_KEY)); } catch { parsed = null; }
  if (!parsed) return null;
  const normalized = normalizeUiState(parsed);
  if (!normalized.savedAt || now - normalized.savedAt > maxAgeMs || now < normalized.savedAt) return null;
  return normalized;
};

export const clearUiState = (storage = sessionStore()) => {
  try { storage?.removeItem(VENAQQI_UI_STATE_KEY); } catch { /* storage unavailable */ }
};

export const isMapsDestination = value => {
  const href = String(value || "").trim();
  if (!href) return false;
  try {
    const url = new URL(href, typeof window !== "undefined" ? window.location.origin : "https://venaqqi.invalid");
    const hostname = url.hostname.toLowerCase();
    return url.protocol === "geo:"
      || hostname === "maps.app.goo.gl"
      || hostname === "share.google"
      || (hostname === "goo.gl" && url.pathname.startsWith("/maps"))
      || hostname.startsWith("maps.google.")
      || (/(^|\.)google\.[a-z.]+$/.test(hostname) && url.pathname.startsWith("/maps"));
  } catch { return false; }
};

export const markActiveNow = (storage = localStore(), now = Date.now()) => {
  try { storage?.setItem(VENAQQI_LAST_ACTIVE_KEY, String(now)); } catch { /* storage unavailable */ }
};

export const inactivityDays = (storage = localStore(), now = Date.now()) => {
  let last = 0;
  try { last = Number(storage?.getItem(VENAQQI_LAST_ACTIVE_KEY)) || 0; } catch { last = 0; }
  if (!last || now < last) return null;
  return Math.floor((now - last) / (24 * 60 * 60 * 1000));
};

export const shouldOfferTutorialRefresh = (storage = localStore(), now = Date.now(), inactiveDays = 30) => {
  let completed = 0;
  let last = 0;
  try {
    completed = Number(storage?.getItem(VENAQQI_TUTORIAL_COMPLETED_KEY)) || 0;
    last = Number(storage?.getItem(VENAQQI_LAST_ACTIVE_KEY)) || 0;
  } catch { return false; }
  if (!completed || !last || now < last) return false;
  return now - last >= inactiveDays * 24 * 60 * 60 * 1000;
};

export const saveTutorialProgress = (step, storage = localStore(), now = Date.now()) => {
  const normalizedStep = Math.max(1, Math.min(3, Math.floor(Number(step) || 1)));
  const value = { step: normalizedStep, updatedAt: now };
  try { storage?.setItem(VENAQQI_TUTORIAL_PROGRESS_KEY, JSON.stringify(value)); } catch { /* storage unavailable */ }
  return value;
};

export const readTutorialProgress = (storage = localStore()) => {
  let parsed;
  try { parsed = safeJsonParse(storage?.getItem(VENAQQI_TUTORIAL_PROGRESS_KEY)); } catch { parsed = null; }
  const step = Math.floor(Number(parsed?.step) || 0);
  if (step < 1 || step > 3) return null;
  return { step, updatedAt: Number(parsed?.updatedAt) || 0 };
};

export const clearTutorialProgress = (storage = localStore()) => {
  try { storage?.removeItem(VENAQQI_TUTORIAL_PROGRESS_KEY); } catch { /* storage unavailable */ }
};

export const markTutorialCompleted = (storage = localStore(), now = Date.now()) => {
  try {
    storage?.setItem(VENAQQI_TUTORIAL_COMPLETED_KEY, String(now));
    storage?.removeItem(VENAQQI_TUTORIAL_PROGRESS_KEY);
  } catch { /* storage unavailable */ }
};